import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Link2 } from 'lucide-react';
import { createCatalogItem, updateItem } from '../services/api';
import { linkableCatalogItems } from '../utils/coverage';
import { trackEvent } from '../utils/analytics';
import useEscapeKey from '../hooks/useEscapeKey';
import { inventoryErrorMessage } from '../utils/inventoryErrors';
import './ResaleLinkModal.css';

/**
 * Vincula un producto de la carta como reventa (RES-39): cada venta descuenta N unidades de un
 * insumo. Por defecto crea un insumo "espejo" con el mismo nombre; también deja elegir uno que
 * ya exista en el catálogo.
 * @param {{product: Object, catalogItems?: Object[], canCreate?: boolean, onClose: Function, onLinked: Function}} props - Props
 * @returns {JSX.Element} Modal
 */
function ResaleLinkModal({ product, catalogItems = [], canCreate = true, onClose, onLinked }) {
  const { t } = useTranslation();
  const options = useMemo(() => linkableCatalogItems(catalogItems), [catalogItems]);
  const [mode, setMode] = useState(canCreate ? 'new' : 'existing');
  const [inventoryItemId, setInventoryItemId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEscapeKey(true, () => {
    if (!saving) onClose();
  });

  const selected = options.find((item) => item.itemId === inventoryItemId);
  const unit = mode === 'existing' && selected ? selected.unit || 'un' : 'un';

  const handleSubmit = async (e) => {
    e.preventDefault();
    const qty = parseFloat(quantity);
    if (!(qty > 0)) {
      setError(t('inventory.coverage.linkModal.quantityInvalid'));
      return;
    }
    if (mode === 'existing' && !selected) {
      setError(t('inventory.coverage.linkModal.selectItemError'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      let targetId = selected?.itemId;
      let created = null;
      if (mode === 'new') {
        created = await createCatalogItem({ name: product.name, kind: 'RESALE', unit: 'un' });
        targetId = created?.itemId;
        if (!targetId) throw new Error('missing itemId');
      }
      const inventoryLink = { inventoryItemId: targetId, quantity: qty, unit };
      await updateItem(product.itemId, { inventoryLink });
      trackEvent('inventory_link_created', { kind: 'RESALE' });
      onLinked({ product, inventoryLink, createdItem: created });
    } catch (err) {
      console.error('Error linking resale product:', err);
      setError(inventoryErrorMessage(t, err, 'inventory.coverage.linkModal.error'));
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={() => !saving && onClose()}>
      <div
        className="modal-content modal-small resale-link-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="resale-link-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 id="resale-link-title">{t('inventory.coverage.linkModal.title')}</h2>
          <button
            type="button"
            className="close-btn"
            onClick={onClose}
            disabled={saving}
            aria-label={t('common.close')}
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>
        <form onSubmit={handleSubmit} noValidate>
          <div className="modal-body">
            <p className="resale-link-product">
              {t('inventory.coverage.linkModal.intro', { name: product.name })}
            </p>

            {error && (
              <div className="alert alert-error" role="alert">
                {error}
              </div>
            )}

            <div
              className="resale-link-modes"
              role="radiogroup"
              aria-label={t('inventory.coverage.linkModal.title')}
            >
              {canCreate && (
                <label className={`resale-link-mode${mode === 'new' ? ' selected' : ''}`}>
                  <input
                    type="radio"
                    name="resale-link-mode"
                    value="new"
                    checked={mode === 'new'}
                    onChange={() => setMode('new')}
                  />
                  <span>
                    <strong>{t('inventory.coverage.linkModal.modeNew')}</strong>
                    <small>
                      {t('inventory.coverage.linkModal.modeNewHelp', { name: product.name })}
                    </small>
                  </span>
                </label>
              )}
              <label
                className={`resale-link-mode${mode === 'existing' ? ' selected' : ''}${
                  options.length === 0 ? ' disabled' : ''
                }`}
              >
                <input
                  type="radio"
                  name="resale-link-mode"
                  value="existing"
                  checked={mode === 'existing'}
                  disabled={options.length === 0}
                  onChange={() => setMode('existing')}
                />
                <span>
                  <strong>{t('inventory.coverage.linkModal.modeExisting')}</strong>
                  <small>
                    {options.length === 0
                      ? t('inventory.coverage.linkModal.noCatalogItems')
                      : t('inventory.coverage.linkModal.modeExistingHelp')}
                  </small>
                </span>
              </label>
            </div>

            {mode === 'existing' && (
              <div className="form-group">
                <label htmlFor="resale-link-item">{t('inventory.coverage.linkModal.item')} *</label>
                <select
                  id="resale-link-item"
                  value={inventoryItemId}
                  onChange={(e) => setInventoryItemId(e.target.value)}
                >
                  <option value="">{t('inventory.coverage.linkModal.selectItem')}</option>
                  {options.map((item) => (
                    <option key={item.itemId} value={item.itemId}>
                      {item.name}
                      {item.kind === 'RESALE'
                        ? ` · ${t('inventory.kinds.RESALE')}`
                        : ` · ${t(`inventory.kinds.${item.kind || 'INGREDIENT'}`)}`}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="resale-link-quantity">
                {t('inventory.coverage.linkModal.quantity')} *
              </label>
              <div className="resale-link-quantity">
                <input
                  id="resale-link-quantity"
                  type="number"
                  min="0"
                  step="0.01"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                />
                <span className="resale-link-unit">{unit}</span>
              </div>
              <small className="help-text">{t('inventory.coverage.linkModal.quantityHelp')}</small>
            </div>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              <Link2 size={16} aria-hidden="true" />
              {saving
                ? t('inventory.coverage.linkModal.saving')
                : t('inventory.coverage.linkModal.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ResaleLinkModal;
